import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { usePaymentPolling } from "@/hooks/usePaymentPolling";

function PollingView() {
  return (
    <>
      <Loader2 className="h-10 w-10 text-orange-500 mx-auto mb-4 animate-spin" />
      <h1 className="font-display text-2xl font-bold text-slate-900">Confirming payment…</h1>
      <p className="text-slate-500 mt-2">This usually takes a few seconds.</p>
    </>
  );
}

function PaidView({ info, onContinue }) {
  const subline =
    info?.package_type === "subscription"
      ? `Your ${info.package_name} plan is active.`
      : "Your pay-per-quote credit has been added.";
  return (
    <>
      <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
      <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="payment-paid">
        Payment confirmed.
      </h1>
      <p className="text-slate-600 mt-2">{subline}</p>
      <Button
        onClick={onContinue}
        className="mt-6 bg-orange-500 hover:bg-orange-600 text-white"
        data-testid="go-to-dashboard"
      >
        Go to dashboard
      </Button>
    </>
  );
}

const FAILED_HEADLINE = {
  expired: "Session expired",
  timeout: "Still processing",
  error: "Something went wrong",
};

function FailedView({ status, info, onRetry }) {
  const headline = FAILED_HEADLINE[status] || "Something went wrong";
  const subline =
    status === "timeout"
      ? "Your payment is still being confirmed. Check your email or refresh in a moment."
      : info?.message || "Please try again or contact support.";
  return (
    <>
      <XCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
      <h1 className="font-display text-2xl font-bold text-slate-900">{headline}</h1>
      <p className="text-slate-600 mt-2">{subline}</p>
      <Button onClick={onRetry} variant="outline" className="mt-6" data-testid="back-to-pricing">
        Back to pricing
      </Button>
    </>
  );
}

export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const { status, info } = usePaymentPolling(sessionId);

  return (
    <div className="max-w-xl mx-auto" data-testid="billing-success-page">
      <Card className="p-10 text-center border-slate-200">
        {status === "polling" && <PollingView />}
        {status === "paid" && <PaidView info={info} onContinue={() => navigate("/app")} />}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <FailedView status={status} info={info} onRetry={() => navigate("/pricing")} />
        )}
      </Card>
    </div>
  );
}
